import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Button,
} from "antd";
import {
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { API_URL } from "../../url_config";
import { NavigationStepsAll } from "../../components/navbars/navbar_all";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Titres centralisés ===
const columnTitles = {
  ligne: "Ligne",
  coquille: "Coquille à vérifier",
  configuration_ad: "Configuration AD SAE/VE3",
  remontee_de_ad_sae_tr: "Remontée de AD SAE-TR",
  geolocalisation: "Géolocalisation AD SAE",
  gestion_ad: "Acquittement / Clôture SAE",
  remontee_de_ad_tetra: "Remontée de AD TETRA",
  ecoute_d_ambiance: "Écoute d’ambiance",
  prise_en_compte_appel: "Appel individuel",
  signal: "État",
  comment: "Commentaire",
  aide: "Aide (Supervision)",
};

// === Champ fluide (coquille / commentaire / aide) ===
const EditableField = ({ recordId, field, initialValue, onPatch, type = "input", placeholder }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const [status, setStatus] = useState("idle");

  useEffect(() => setLocalValue(initialValue || ""), [initialValue]);

  const debouncedPatch = useCallback(
    debounce(async (val) => {
      setStatus("saving");
      try {
        await onPatch(recordId, field, val);
        setStatus("success");
        setTimeout(() => setStatus("idle"), 800);
      } catch (err) {
        console.error("Erreur PATCH :", err);
        setStatus("error");
        message.error("Erreur lors de la sauvegarde");
      }
    }, 600),
    [recordId, field, onPatch]
  );

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    debouncedPatch(newVal);
  };

  return (
    <Space style={{ width: "100%" }}>
      {type === "textarea" ? (
        <TextArea
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
          autoSize={{ minRows: 1, maxRows: 3 }}
          style={{ borderRadius: "6px" }}
        />
      ) : (
        <Input
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
          style={{ width: "100%", borderRadius: "6px" }}
        />
      )}
      {status === "saving" && <SyncOutlined spin style={{ color: "#007bff" }} />}
      {status === "success" && <CheckCircleOutlined style={{ color: "#52c41a" }} />}
      {status === "error" && <CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
    </Space>
  );
};

// === Bloc détails commentaire/aide (expandable) ===
const TableGeolocalisation = ({ record, onPatch }) => (
  <div style={{ padding: "20px" }}>
    <Row gutter={16}>
      <Col span={12}>
        <Card title={columnTitles.comment} bordered>
          <EditableField
            recordId={record.id}
            field="comment"
            initialValue={record.comment}
            onPatch={onPatch}
            type="textarea"
            placeholder="Saisis un commentaire..."
          />
        </Card>
      </Col>
      <Col span={12}>
        <Card title={columnTitles.aide} bordered>
          <EditableField
            recordId={record.id}
            field="aide"
            initialValue={record.aide}
            onPatch={onPatch}
            type="textarea"
            placeholder="Décris ton besoin d’aide..."
          />
        </Card>
      </Col>
    </Row>
  </div>
);

// === Composant principal ===
const ValidationGeolocalisationAll = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/validations/validation_geolocalisation/`);
      setData(res.data.map((item, index) => ({ key: index, ...item })));
    } catch (error) {
      console.error("Erreur de chargement :", error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePatch = useCallback(async (id, field, value) => {
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
    try {
      await axios.patch(`${API_URL}/validations/validation_geolocalisation/${id}/`, { [field]: value });
    } catch (error) {
      console.error("Erreur PATCH :", error);
      message.error("Erreur lors de la mise à jour");
    }
  }, []);

  const columns = [
    { title: columnTitles.ligne, dataIndex: "ligne" },
    {
      title: columnTitles.configuration_ad,
      dataIndex: "configuration_ad",
      render: (val, record) => (
        <Select
          value={val || ""}
          onChange={(value) => handlePatch(record.id, "configuration_ad", value)}
          style={{ width: 120 }}
          options={[
            { value: "", label: " " },
            { value: "ok", label: <span style={{ color: "#52c41a" }}>OK</span> },
            { value: "ko", label: <span style={{ color: "#ff4d4f" }}>KO</span> },
          ]}
        />
      ),
    },
    {
      title: columnTitles.coquille,
      dataIndex: "coquille",
      render: (val, record) => (
        <EditableField
          recordId={record.id}
          field="coquille"
          initialValue={val}
          onPatch={handlePatch}
          placeholder="Saisir une coquille..."
        />
      ),
    },
    ...[
      "remontee_de_ad_sae_tr",
      "geolocalisation",
      "gestion_ad",
    ].map((field) => ({
      title: columnTitles[field],
      dataIndex: field,
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => handlePatch(record.id, field, e.target.checked)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    })),
    {
      title: columnTitles.signal,
      dataIndex: "signal",
      render: (val, record) => {
        const color = val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";
        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handlePatch(record.id, "signal", nextVal);
        };
        return (
          <div
            onClick={handleClick}
            style={{ cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "16px",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px", position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Chargement..." />
        </div>
      )}

      <NavigationStepsAll selectedKey="geolocalisation" />
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        onClick={() => {
          setRefreshing(true);
          fetchData();
        }}
        loading={refreshing}
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 10000,
          backgroundColor: "#007bff",
          borderColor: "#007bff",
          fontWeight: 500,
        }}
      >
        Rafraîchir
      </Button>

      <Table
        columns={columns}
        dataSource={data}
        expandable={{
          expandedRowRender: (record) => (
            <TableGeolocalisation record={record} onPatch={handlePatch} />
          ),
        }}
        bordered
        pagination={false}
        rowClassName={() => "custom-row"}
        style={{ border: "2px solid #bfbfbf", borderRadius: "12px", overflow: "hidden", marginTop: 60 }}
      />

      <div style={{ padding: 20 }}>
        <ModifyItemPage
          apiUrl={`${API_URL}/validations/validation_geolocalisation/`}
          ot_tag="cds"
          title="Gestion"
          rowKey="id"
          trigger={<Button icon={<EditOutlined />} shape="circle" />}
          columns={[{ title: columnTitles.ligne, dataIndex: "ligne", key: "ligne" }]}
          formFields={[{ name: "ligne", label: columnTitles.ligne, placeholder: "Entrez la ligne" }]}
        />
      </div>
    </div>
  );
};

export default ValidationGeolocalisationAll;
